import React, { createContext, useContext, useState, useCallback } from 'react';
import AlertDialog from '../pages/components/AlertDialog';
import ConfirmDialog from '../pages/components/ConfirmDialog';
import ErrorAlert from '../pages/components/ErrorAlert';
import TextInputDialog from '../pages/components/TextInputDialog';
import ErrorBoundary from '../ErrorBoundary';

const AlertContext = createContext();

export const useAlert = () => {
    const { setAlert } = useContext(AlertContext);

    const showAlert = useCallback((title, message) => {
        setAlert({ title, message });
    }, [setAlert]);

    return showAlert;
};

export const useConfirm = () => {
    const { setConfirm } = useContext(AlertContext);

    const showConfirm = useCallback((title, message, onConfirm, onCancel) => {
        setConfirm({ title, message, onConfirm, onCancel });
    }, [setConfirm]);

    return showConfirm;
};

export const useTextInput = () => {
    const { setTextInput } = useContext(AlertContext);

    const showTextInput = useCallback((title, message, onConfirm) => {
        setTextInput({ title, message, onConfirm });
    }, [setTextInput]);

    return showTextInput;
};

export const useErrorAlert = () => {
    const { setErrorAlert } = useContext(AlertContext);

    const showErrorAlert = useCallback((title, message, detail) => {
        setErrorAlert({ title, message, detail });
    }, [setErrorAlert]);

    return showErrorAlert;
};

export const AlertProvider = ({ children }) => {
    const [alert, setAlert] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [textInput, setTextInput] = useState(null);
    const [errorAlert, setErrorAlert] = useState(null);

    const handleAlertClose = useCallback(() => {
        setAlert(null);
    }, []);

    const handleConfirm = useCallback(() => {
        const onConfirm = confirm.onConfirm;
        setConfirm(null);
        onConfirm();
    }, [confirm]);

    const handleConfirmCancel = useCallback(() => {
        const onCancel = confirm.onCancel;
        setConfirm(null);
        if (onCancel) {
            onCancel();
        }
    }, [confirm]);

    const handleConfirmClose = useCallback(() => {
        setConfirm(null);
    }, []);

    const handleTextInput = useCallback((text) => {
        const onConfirm = textInput.onConfirm;
        setTextInput(null);
        onConfirm(text);
    }, [textInput]);

    const handleTextInputClose = useCallback(() => {
        setTextInput(null);
    }, []);

    const handleErrorAlertClose = useCallback(() => {
        setErrorAlert(null);
    }, []);

    return (
        <AlertContext.Provider value={{ setAlert, setConfirm, setTextInput, setErrorAlert }}>
            {children}
            <ErrorBoundary>
                <AlertDialog alert={alert} onClose={handleAlertClose} />
                <ConfirmDialog prompt={confirm} onConfirm={handleConfirm} onCancel={handleConfirmCancel} onClose={handleConfirmClose} />
                <TextInputDialog prompt={textInput} onConfirm={handleTextInput} onClose={handleTextInputClose} />
                <ErrorAlert error={errorAlert} onClose={handleErrorAlertClose} />
            </ErrorBoundary>
        </AlertContext.Provider>
    );
};
